/**
 * Image URL helpers.
 *
 * `originalImage` is reserved for SEO, structured data and feeds.
 * `optimizeImage`/`srcSet` are for visual UI only: they use the same-origin
 * image proxy to deliver right-sized WebP thumbnails and avoid downloading
 * multi-megapixel originals into small mobile cards.
 */
const MARKER = "/storage/v1/object/public/";
const RENDER_MARKER = "/storage/v1/render/image/public/";
const PROXY_MARKER = "/api/public/img/";

function renderBase(url: string): string | null {
  const renderIdx = url.indexOf(RENDER_MARKER);
  const idx = renderIdx >= 0 ? renderIdx : url.indexOf(MARKER);
  if (idx === -1) return null;
  const [base] = url.split("?");
  const marker = renderIdx >= 0 ? RENDER_MARKER : MARKER;
  const rendered = base.slice(base.indexOf(marker) + marker.length);
  return `${PROXY_MARKER}${rendered}`;
}

/** Original, untouched file through the same-origin proxy. */
export function originalImage(url: string | null | undefined): string {
  if (!url) return "";
  const proxy = url.match(/^(https?:\/\/[^/]+)?\/api\/public\/img\/(.+?)(?:\?.*)?$/i);
  if (proxy) return `${proxy[1] ?? ""}${PROXY_MARKER}${proxy[2]}`;
  return renderBase(url) ?? url;
}

function proxyPath(url: string | null | undefined): string | null {
  const original = originalImage(url);
  const match = original.match(/^(https?:\/\/[^/]+)?\/api\/public\/img\/(.+)$/i);
  return match ? `${match[1] ?? ""}${PROXY_MARKER}${match[2].split("?")[0]}` : null;
}

function directRenderPath(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const renderIndex = parsed.pathname.indexOf(RENDER_MARKER);
    const objectIndex = parsed.pathname.indexOf(MARKER);
    const marker = renderIndex >= 0 ? RENDER_MARKER : MARKER;
    const index = renderIndex >= 0 ? renderIndex : objectIndex;
    if (index < 0) return null;
    return `${parsed.origin}${RENDER_MARKER}${parsed.pathname.slice(index + marker.length)}`;
  } catch {
    return null;
  }
}

function transformUrl(
  path: string,
  width: number,
  quality: number,
  resize: "contain" | "cover",
  height?: number,
): string {
  const params = new URLSearchParams({
    width: String(Math.max(1, Math.round(width))),
    quality: String(Math.min(100, Math.max(1, Math.round(quality)))),
    resize,
    format: "webp",
  });
  if (height) params.set("height", String(Math.max(1, Math.round(height))));
  return `${path}?${params}`;
}

// The same image URLs are recomputed on every render of every card. The
// result depends only on the arguments, so cache it and build it once.
const srcSetCache = new Map<string, string>();
const SRCSET_CACHE_LIMIT = 1000;

/**
 * Right-sized visual thumbnail. The image endpoint marks transformed URLs
 * `noimageindex`; SEO/Google URLs continue to use originalImageUrl().
 */
export function optimizeImage(
  url: string | null | undefined,
  width = 400,
  quality = 85,
  resize: "contain" | "cover" = "contain",
  height?: number,
): string {
  const cacheKey = `o|${url ?? ""}|${width}|${quality}|${resize}|${height ?? ""}`;
  const cached = srcSetCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const path = proxyPath(url);
  const result = path ? transformUrl(path, width, quality, resize, height) : originalImage(url);
  if (srcSetCache.size >= SRCSET_CACHE_LIMIT) srcSetCache.clear();
  srcSetCache.set(cacheKey, result);
  return result;
}

/** Direct Supabase transform for the critical hero only; falls back safely. */
export function directOptimizeImage(
  url: string | null | undefined,
  width = 400,
  quality = 85,
  resize: "contain" | "cover" = "contain",
  height?: number,
): string {
  // Always same-origin: supabase.co cannot send noimageindex headers.
  return optimizeImage(url, width, quality, resize, height);
}

/** Build responsive UI thumbnail candidates for Supabase-hosted images. */
export function srcSet(
  url: string | null | undefined,
  widths: number[] = [480, 768, 1080, 1600],
  quality = 85,
  resize: "contain" | "cover" = "contain",
  aspectRatio?: number,
): string {
  const cacheKey = `${url ?? ""}|${widths.join(",")}|${quality}|${resize}|${aspectRatio ?? ""}`;
  const cached = srcSetCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const path = proxyPath(url);
  if (!path) return "";
  const uniqueWidths = [...new Set(widths.map((w) => Math.max(1, Math.round(w))))].sort(
    (a, b) => a - b,
  );
  const result = uniqueWidths
    .map((width) => {
      const params = new URLSearchParams({
        width: String(width),
        quality: String(Math.min(100, Math.max(1, Math.round(quality)))),
        resize,
        format: "webp",
      });
      if (aspectRatio && aspectRatio > 0)
        params.set("height", String(Math.max(1, Math.round(width / aspectRatio))));
      return `${path}?${params} ${width}w`;
    })
    .join(", ");
  if (srcSetCache.size >= SRCSET_CACHE_LIMIT) srcSetCache.clear();
  srcSetCache.set(cacheKey, result);
  return result;
}

/** Responsive direct candidates for the critical hero image. */
export function directSrcSet(
  url: string | null | undefined,
  widths: number[] = [480, 768, 1080],
  quality = 85,
  resize: "contain" | "cover" = "contain",
  aspectRatio?: number,
): string {
  return srcSet(url, widths, quality, resize, aspectRatio);
}
